<template>
	<div
		class="c-accordion"
		:class="['c-accordion--' + version, { 'c-accordion--open': isOpen }]"
	>
		<div class="c-accordion__bar" @click="isOpen = !isOpen">
			<h3>
				<span v-if="title">{{ title }}</span>
				<slot name="bar"></slot>
			</h3>
			<ArrowIcon :class="{ open: isOpen }"></ArrowIcon>
		</div>
		<div class="wrapper" :class="{ 'wrapper--expanded': isOpen, 'wrapper--closed': !isOpen }">
			<div class="inner">
				<div class="c-accordion__content">
					<div
						class="c-accordion__text"
						:class="{
							'c-accordion__text--collapsed': !showFullText && truncateText,
						}"
						:style="contentStyle"
					>
						<div ref="innerContent">
							<slot name="contents"></slot>
						</div>
					</div>
					<button
						v-if="truncateText && !showFullText"
						class="c-accordion__show-more"
						@click.stop="showFullText = !showFullText"
					>
						{{
							translations["ReadMore"]
						}}
					</button>
				</div>
				<div
					class="c-accordion__overlay"
					@click="isOpen = !isOpen"
					v-if="overlay && isOpen"
				></div>
			</div>
		</div>
	</div>
</template>

<script>
import ArrowIcon from "@web/assets/icon/arrow.svg";

export default {
	name: "Accordion",
	props: {
		title: {
			type: String,
			default: "",
		},
		contents: String,
		propOpen: {
			type: Boolean,
			default: false,
		},
		checkOpen: {
			type: Boolean,
			default: false,
		},
		overlay: {
			type: Boolean,
			default: false,
		},
		version: {
			type: String,
			default: "lines",
		},
		defaultOpen: {
			type: Boolean,
			default: false,
		},
		truncateText: {
			type: Boolean,
			default: false,
		},
	},
	components: {
		ArrowIcon,
	},
	watch: {
		checkOpen() {
			if (this.checkOpen) {
				this.isOpen = this.propOpen;
			}
		},
		// Set the default open state, if it is set
		defaultOpen: {
			immediate: true,
			handler(newVal) {
				this.isOpen = newVal;
			},
		},
		// If the text should be truncated, set the height to 3 lines of text
		truncateText: {
			immediate: true,
			handler(newVal) {
				if (newVal) {
					this.contentHeight = "4em"; // Aprox 3 lines of text
				}
			}
		},
	},
	data: () => {
		return {
			isOpen: false,
			showFullText: false,
			contentHeight: "auto",
			fullHeight: null,
			isTransitioning: false,
		};
	},
	
	computed: {
		// Set the height of the content based on the current state
		contentStyle() {
			return {
				height: this.showFullText ? this.fullHeight : this.contentHeight,
				transition: 'height 0.3s ease-out'
			}
		}
  	},

	created() {},

	mounted() {
		// Initial measurement of the content height
		if (this.$refs.innerContent) {
			this.fullHeight = this.$refs.innerContent.offsetHeight + 'px';
		}
  	}
};
</script>

<style lang="scss">
@import "@backend/scss/abstracts/variables.scss";
@import "@backend/scss/abstracts/mixins.scss";

.c-accordion {
	width: 100%;
	
	// Grid trick to animate the height of the text content
	.wrapper {
		display: grid;
		transition: grid-template-rows 0.3s ease-out;
		grid-template-rows: 0fr;

		&--expanded {
			grid-template-rows: 1fr;
		}

		&--closed {
			grid-template-rows: 0fr;
		}

		.inner {
			overflow: hidden;
			min-height: 0;
		}
	}

	&__bar {
		display: flex;
		justify-content: space-between;
		cursor: pointer;
		align-items: center;

		h3 {
			padding-right: 30px;
		}

		svg {
			width: 24px;
			transform: rotate(180deg);
		}

		.open {
			transform: rotate(0deg);
		}
	}

	&--lines {
		border-top: 1px solid rgb(234, 233, 233);
		.c-accordion {
			&__bar {
				padding: 24px 0px 24px 0px;

				h3 {
					font-family: $font-heading;
					font-size: 20px;
					line-height: 33px;
					font-weight: $f-bold;
					color: $font-heading-color;
				}
			}
			&__content {
				min-height: 0px;
				padding-bottom: 30px;
				text-align: left;
			}
		}

		&:last-of-type {
			border-bottom: 1px solid rgb(234, 233, 233);
		}
	}

	&--block {
		background-color: $c-greylight;
		margin-bottom: 8px;
		.c-accordion {
			&__bar {
				padding: 19px 24px;
			}
			&__content {
				padding: 0 24px 19px 24px;
			}
		}
	}

	&--menu {
		background-color: white;
		margin-top: 12px;
		margin-bottom: 12px;
		.c-accordion {
			&__bar {
				align-items: center;
				h3 {
					margin-bottom: 0;
				}
			}
			&__menu-links {
				padding-top: 8px;
				li {
					padding-left: 16px;
				}
			}
		}
	}

	&__text {
		overflow: hidden;
		will-change: height;

    &--collapsed {
      position: relative;

      &::after {
        content: '';
        position: absolute;
				bottom: -4px;
        left: 0;
        width: 100%;
        height: 2em;
        background: linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1));
      }
    }
  }

  &__show-more {
    margin-top: 12px;
    background: none;
    border: none;
    color: $c-blue;
	font-weight: $f-semibold;
    padding: 0;
    cursor: pointer;

	@include hover-underline($reverse: true);
  }
}
</style
